import * as React from 'react';

import { translate } from '~l10n/client';

import { IconButton } from '~shared/components/buttons';
import { IconFolder, IconPencil } from '~shared/components/icons/standard';
import { MenuDivider, StyledMenuItem } from '~shared/components/Menu';
import { TooltipV2 } from '~shared/components/TooltipV2';

import { Folder } from '../../../../api/quiz/getQuizzes/types';
import {
  useLibraryFolderActions,
  useTrashFolderActions,
} from './ActionProviders';
import { QuizListItem, QuizListItemBaseProps } from './QuizListItem';
import { QuizListMenuList } from './QuizListMenuList';

export type FolderProps = QuizListItemBaseProps & Folder;

export function QuizListFolder({
  uniqueId,
  name,
  deleted,
  disableHover,

  ...props
}: FolderProps) {
  return (
    <QuizListItem
      {...props}
      uniqueId={uniqueId}
      disableHover={disableHover}
      itemIcon={
        <IconFolder
          className="flex text-blue-900 ml-1"
          title={translate('Folder')}
          titleId={`icon-title-${uniqueId}`}
        />
      }
      href={deleted ? undefined : `#quizzes/${uniqueId}`}
      shortcutActions={
        deleted ? null : getLibraryShortcuts({ uniqueId: uniqueId })
      }
      actionsMenu={
        deleted ? (
          <TrashActionsMenu uniqueId={uniqueId} />
        ) : (
          <LibraryActionsMenu uniqueId={uniqueId} />
        )
      }
    >
      {name}
    </QuizListItem>
  );
}

interface LibraryShortcutsProps {
  uniqueId: string;
}

function getLibraryShortcuts({ uniqueId }: LibraryShortcutsProps) {
  return [
    <RenameFolderShortcut key="rename-folder-shortcut" uniqueId={uniqueId} />,
  ];
}

const RenameFolderShortcut = ({ uniqueId }: { uniqueId: string }) => {
  const { openRenameFolder } = useLibraryFolderActions();
  const handleOpenRenameFolder = React.useCallback(
    () => openRenameFolder(uniqueId),
    [openRenameFolder, uniqueId]
  );

  return (
    <TooltipV2 key={`tooltip-rename-${uniqueId}`} label={translate('Rename')}>
      <IconButton
        key={'rename'}
        aria-label={translate('Rename')}
        onClick={handleOpenRenameFolder}
        tabIndex={-1}
      >
        <IconPencil />
      </IconButton>
    </TooltipV2>
  );
};
interface LibraryActionsProps {
  uniqueId: string;
}

function LibraryActionsMenu({ uniqueId }: LibraryActionsProps) {
  const { openRenameFolder, deleteFolder, openMoveDialog } =
    useLibraryFolderActions();

  const moveClick = React.useCallback(
    () => openMoveDialog(uniqueId),
    [uniqueId, openMoveDialog]
  );

  const renameClick = React.useCallback(
    () => openRenameFolder(uniqueId),
    [uniqueId, openRenameFolder]
  );

  const deleteClick = React.useCallback(
    () => deleteFolder(uniqueId),
    [uniqueId, deleteFolder]
  );

  return (
    <QuizListMenuList>
      <StyledMenuItem onClick={renameClick}>
        {translate('Rename')}
      </StyledMenuItem>
      <StyledMenuItem onClick={moveClick}>
        {translate('Move to…')}
      </StyledMenuItem>
      <MenuDivider />
      <StyledMenuItem onClick={deleteClick}>
        {translate('Delete')}
      </StyledMenuItem>
    </QuizListMenuList>
  );
}

interface TrashActionsProps {
  uniqueId: string;
}

function TrashActionsMenu({ uniqueId }: TrashActionsProps) {
  const { restoreFolder, purgeFolder } = useTrashFolderActions();
  const onRestore = React.useCallback(
    () => restoreFolder(uniqueId),
    [uniqueId, restoreFolder]
  );

  const onPurge = React.useCallback(
    () => purgeFolder(uniqueId),
    [purgeFolder, uniqueId]
  );

  return (
    <QuizListMenuList>
      <StyledMenuItem onClick={onRestore}>
        {translate('Restore')}
      </StyledMenuItem>
      <MenuDivider />
      <StyledMenuItem onClick={onPurge} isDestructive={true}>
        {translate('Delete Forever')}
      </StyledMenuItem>
    </QuizListMenuList>
  );
}
